import React from "react"
import _ from "lodash"
import Form from "react-bootstrap/Form"
import Row from "react-bootstrap/Row"
import Col from "react-bootstrap/Col"

import { GetApi } from "../../../../../api/api"
import {
  AttributeTypeGroup,
  FeatureTypeReference,
} from "../../../../../api/types"
import { Feedback } from "../../../../../components/Validator"
import AppContext from "../../../../../components/AppContext"
import withModal, {
  WithModalProps,
  InjectedProps,
} from "../../../../../components/HOC/WithModal"
import Pending from "../../../../../components/Pending"
import CheckBox from "../../../../../components/CheckBox"
import Badge from "../../../../../components/Badge"
import Button from "../../../../../components/Button"
import StepForm, { Step } from "../../../../../components/StepForm"

import AssociateWithFeatureTypeModal from "./AssociateWithFeatureTypeModal"
import AttributeTypeValidator from "./AttributeTypeValidator"
import Properties from "../../Properties/Properties"
import { UiTypes, ApiToUi } from "../../Properties/components/UIModel"

const nameId = "nameInputPropertySet"
const descriptionId = "descriptionInputPropertySet"
const checkFtId = "checkSelectFeatureType"

interface InnerEditProps extends EditProps {
  attributeTypeGroup: AttributeTypeGroup
  associations: FeatureTypeReference[]
  attributeTypes: UiTypes.CustomAttributeType[]
  associateWithAllFeatureTypes?: boolean
}

interface InnerEditState {
  attributeTypeGroup: AttributeTypeGroup
  associations: FeatureTypeReference[]
  attributeTypes: UiTypes.CustomAttributeType[]
  checkedAssociateWithAll?: boolean
  showAssociateWithFeatureType: boolean
  valid: Feedback
  disabled: boolean
}

class InnerEdit extends React.Component<InnerEditProps, InnerEditState> {
  public static contextType = AppContext

  constructor(props: InnerEditProps) {
    super(props)
    this.state = {
      attributeTypeGroup: props.attributeTypeGroup,
      associations: props.associations,
      attributeTypes: props.attributeTypes,
      checkedAssociateWithAll: props.associateWithAllFeatureTypes,
      showAssociateWithFeatureType: false,
      valid: props.valid,
      disabled: props.disabled,
    }
  }

  public componentDidUpdate(prevProps: InnerEditProps) {
    if (!_.isEqual(prevProps, this.props)) {
      this.setState({
        valid: this.props.valid,
        disabled: this.props.disabled,
      })
    }
  }

  public render() {
    return (
      <StepForm>
        <Step name="Basic information">{this.renderBasicInfo()}</Step>
        <Step name="Properties">{this.renderProperties()}</Step>
        <Step name="Associate with feature types">
          {this.renderAssociateWithFeatureTypes()}
        </Step>
      </StepForm>
    )
  }

  private renderBasicInfo = () => {
    const disabledName =
      this.state.disabled ||
      (!this.state.valid.valid && this.state.valid.id !== nameId)
    const disabledDescription =
      this.state.disabled ||
      (!this.state.valid.valid && this.state.valid.id !== descriptionId)

    const inValidName = this.state.valid.id === nameId
    const inValidDescription = this.state.valid.id === descriptionId

    return (
      <Form>
        <Form.Group as={Row} controlId={nameId}>
          <Form.Label column={true} md="3">
            Name <span className="text-danger">*</span>
          </Form.Label>
          <Col md="9">
            <Form.Control
              type="text"
              value={this.state.attributeTypeGroup.name}
              onChange={this.setName}
              onBlur={this.updateAttributeTypeGroup}
              disabled={disabledName}
              isInvalid={inValidName}
            />
            <Form.Control.Feedback type="invalid">
              {this.state.valid.message}
            </Form.Control.Feedback>
            <Form.Text>
              Name of the new property set. Changes will reset the feature type
              associations.
            </Form.Text>
          </Col>
        </Form.Group>

        <Form.Group as={Row} controlId={descriptionId}>
          <Form.Label column={true} md="3">
            Description
          </Form.Label>
          <Col md="9">
            <Form.Control
              type="text"
              value={this.state.attributeTypeGroup.description}
              onChange={this.setDescription}
              onBlur={this.updateAttributeTypeGroup}
              disabled={disabledDescription}
              isInvalid={inValidDescription}
            />
            <Form.Control.Feedback type="invalid">
              {this.state.valid.message}
            </Form.Control.Feedback>
            <Form.Text>
              An optional description for the new property set. Changes will
              reset the feature type associations.
            </Form.Text>
          </Col>
        </Form.Group>
      </Form>
    )
  }

  private renderProperties = () => (
    <Form>
      <Form.Group>
        <Properties
          fccId={this.props.fccId}
          atgId={this.props.atgId}
          attributeTypes={this.state.attributeTypes}
          currentVersion={this.props.currentVersion}
          maximumStreamVersion={this.props.maximumStreamVersion}
          // tslint:disable-next-line: jsx-no-lambda
          refresh={async () => {
            const api = await GetApi()
            const attributeTypes = await api.getAttributeTypesByGroup(
              this.props.fccId,
              this.props.atgId
            )
            this.setState({
              attributeTypes: attributeTypes.map(ApiToUi.convertAttributeType),
            })
          }}
          disabled={this.state.disabled}
          setDisabled={this.setDisabled}
        />
      </Form.Group>
    </Form>
  )

  private renderAssociateWithFeatureTypes = () => {
    const disabledCheckFt = this.state.disabled || !this.state.valid.valid

    return (
      <Form>
        <p>
          Either automatically associate this property set to all current and
          future feature types or select and associate the feature types
          manually.
        </p>
        <Form.Group as={Row}>
          <Col md="6" className="mb-3 mb-md-0">
            <CheckBox
              id={checkFtId}
              label="Associate with all feature types"
              onToggle={this.onCheckboxChange}
              disabled={disabledCheckFt}
              checked={this.state.checkedAssociateWithAll}
            />
          </Col>
          <Col md="6">
            {this.state.showAssociateWithFeatureType ? (
              <AssociateWithFeatureTypeModal
                fccId={this.props.fccId}
                atgId={this.props.atgId}
                currentVersion={this.props.currentVersion}
                isOpen={this.state.showAssociateWithFeatureType}
                toggle={this.toggleAssociateWithFeatureType}
                headerText="Edit Feature Type Association"
                toggleText="Done"
                size="xl"
              />
            ) : null}
            <Button
              variant="outline-primary"
              onClick={this.toggleAssociateWithFeatureType}
              block={true}
              disabled={disabledCheckFt || this.state.checkedAssociateWithAll}
            >
              Select feature types
              {!this.state.checkedAssociateWithAll &&
              this.state.associations.length > 0 ? (
                <Badge className="ml-3 px-1" variant="secondary">
                  {this.state.associations.length}
                </Badge>
              ) : null}
            </Button>
          </Col>
        </Form.Group>
      </Form>
    )
  }

  private updateAttributeTypeGroup = async () => {
    if (this.state.valid.valid) {
      this.setDisabled(true)
      try {
        const api = await GetApi()
        await Promise.all([
          api.updateAttributeTypeGroup(
            this.props.fccId,
            this.props.atgId,
            this.state.attributeTypeGroup
          ),
          api.updateAttributeTypeGroupAssociation(
            this.props.fccId,
            this.props.atgId,
            {
              applyToAllFeatureTypes: this.state.checkedAssociateWithAll,
              featureTypeIds: this.state.associations.map(
                (ftr) => ftr.featureTypeId
              ),
            }
          ),
        ])
      } catch (error) {
        this.context.useToasts.addToast(error, "danger")
      } finally {
        this.setDisabled(false)
      }
    }
  }

  private setName = (e: any) => {
    e.persist()
    const isValid = AttributeTypeValidator.getFieldValidator("name")(
      nameId,
      e.target.value
    )
    this.setState(
      (prevState) => ({
        attributeTypeGroup: {
          ...prevState.attributeTypeGroup,
          name: e.target.value,
        },
        valid: isValid,
      }),
      () => this.props.setValid(isValid)
    )
  }

  private setDescription = (e: any) => {
    e.persist()
    const isValid = AttributeTypeValidator.getFieldValidator("description")(
      descriptionId,
      e.target.value
    )
    this.setState(
      (prevState) => ({
        attributeTypeGroup: {
          ...prevState.attributeTypeGroup,
          description: e.target.value,
        },
        valid: isValid,
      }),
      () => this.props.setValid(isValid)
    )
  }

  private onCheckboxChange = (e: any) => {
    e.persist()
    this.setState(
      (prevState) => ({
        checkedAssociateWithAll: !prevState.checkedAssociateWithAll,
      }),
      () => this.updateAttributeTypeGroup()
    )
  }

  private toggleAssociateWithFeatureType = () => {
    this.setState(
      (prevState) => ({
        showAssociateWithFeatureType: !prevState.showAssociateWithFeatureType,
      }),
      async () => {
        if (this.state.showAssociateWithFeatureType === false) {
          const api = await GetApi()
          const associations = await api.getAttributeTypeGroupAssociations(
            this.props.fccId,
            this.props.atgId
          )
          this.setState({
            associations: associations.featureTypeReferences || [],
          })
        }
      }
    )
  }

  private setDisabled = (value: boolean) => {
    this.props.setDisabled(value)
    this.setState({
      disabled: value,
    })
  }
}

interface EditProps extends InjectedProps {
  atgId: number
  fccId: string
  currentVersion: number
  maximumStreamVersion: string
}

const Edit = (props: EditProps) => {
  const getData = async (): Promise<InnerEditProps> => {
    const api = await GetApi()
    const [
      attributeTypeGroup,
      associations,
      attributeTypes,
    ] = await Promise.all([
      api.getAttributeTypeGroup(props.fccId, props.atgId),
      api.getAttributeTypeGroupAssociations(props.fccId, props.atgId),
      api.getAttributeTypesByGroup(props.fccId, props.atgId),
    ])
    return {
      ...props,
      attributeTypeGroup: {
        ...attributeTypeGroup,
        description: attributeTypeGroup.description || "",
      },
      associations: associations.featureTypeReferences || [],
      attributeTypes: attributeTypes.map(ApiToUi.convertAttributeType),
      associateWithAllFeatureTypes: associations.applyToAllFeatureTypes
    }
  }

  const renderSuccess = (innerProps: InnerEditProps) => (
    <InnerEdit {...innerProps} />
  )

  return <Pending getData={getData} renderSuccess={renderSuccess} />
}

export interface EditModalProps extends WithModalProps {
  atgId: number
  fccId: string
  currentVersion: number
}

const EditModal = withModal(Edit)

export default EditModal
